import type { BillingFeature } from '@kanos/domain';
import { PROFILE_GROUPS, type NavSection, type Place } from '@kanos/web-core';
import { FIRM_PROFILE_GROUPS } from './firm-profile-groups';
import {
  Archive,
  BellRing,
  Blocks,
  Building2,
  ClipboardList,
  CreditCard,
  Eraser,
  FileText,
  FileUser,
  FlaskConical,
  Globe,
  GraduationCap,
  House,
  Inbox,
  KeyRound,
  Landmark,
  ListChecks,
  Mail,
  MapPin,
  Palette,
  Receipt,
  Scale,
  Target,
  ScrollText,
  Settings,
  Signature,
  type LucideIcon,
  Users,
  Webhook,
  Workflow,
} from 'lucide-react';
import { CardGroup } from '@kanos/web-core';
import { type ReactNode } from 'react';
import {
  ApiClientsCard,
  AppearanceCard,
  AuditCard,
  BillingCard,
  BillingDetailsCard,
  CatalogCard,
  DisclosureCard,
  DocumentDesignCard,
  DomainsCard,
  ErasureCard,
  ExportCard,
  FactsCard,
  FirmDetailsCard,
  FirmLogoCard,
  InquiryRetentionCard,
  MandateResumptionCard,
  IntakeAutomationCard,
  LegalDocuments,
  LocationsCard,
  MailSettings,
  MembersCard,
  PartnerAppsCard,
  PortalLegalCard,
  ProfessionalsCard,
  ReminderRules,
  SandboxCard,
  TargetProfileCard,
  TeamsCard,
  WebhooksCard,
} from './settings-cards';

/**
 * The main places of the firm app (ADR-037, design direction J): the day, the people on their
 * way to a client - with intakes and inquiries as its tabs - the firm's own obligations, the
 * firm itself - its data and its team - and, as a gear beside it, the settings. Further places
 * join when they are built - nothing empty.
 */
export const MAIN_NAVIGATION: readonly Place[] = [
  { to: '/', label: 'Heute', icon: House },
  { to: '/clients', label: 'Mandanten', icon: Users, also: ['/intakes', '/inquiries'] },
  { to: '/obligations', label: 'Pflichten', icon: ListChecks },
  { to: '/firm', label: 'Kanzlei', icon: Building2 },
  { to: '/settings', label: 'Einstellungen', icon: Settings, iconOnly: true },
];

/** An area of the firm or its settings: one page, one entry of the list beside it, one tile. */
export interface SettingsArea {
  /** The last segment of the address, e.g. `/settings/webhooks`, `/firm/team`. */
  slug: string;
  title: string;
  description: string;
  icon: LucideIcon;
  /** Only administrators of the firm see the area - the API checks every change anyway. */
  adminOnly: boolean;
  /** The product the area comes with (ADR-022); without it, the base - free for every firm. */
  feature?: BillingFeature;
  content: () => ReactNode;
}

type AreaGroup = { key: string; title: string; areas: SettingsArea[] };

/** The firm itself (after Corly's „Kanzlei“): who it is, where, by whom - and its team. */
const FIRM_GROUPS: readonly AreaGroup[] = [
  {
    key: 'firm-data',
    title: 'Stammdaten',
    areas: [
      {
        slug: 'details',
        title: 'Kanzleidaten',
        description: 'Name, Anschrift, Kontakt, Vertretung und Register der Kanzlei.',
        icon: Landmark,
        adminOnly: false,
        content: () => <FirmDetailsCard />,
      },
      {
        slug: 'base-data',
        title: 'Basisdaten',
        description: 'Rechtsform, Tätigkeiten und Größe, mit Herkunft und Nachweisen.',
        icon: Building2,
        adminOnly: false,
        content: () => <FactsCard />,
      },
      {
        slug: 'professionals',
        title: 'Berufsträger',
        description: 'Wer in der Kanzlei berät, mit Beruf und Qualifikation.',
        icon: GraduationCap,
        adminOnly: false,
        content: () => <ProfessionalsCard />,
      },
      {
        slug: 'locations',
        title: 'Standorte',
        description: 'Niederlassungen und Beratungsstellen der Kanzlei.',
        icon: MapPin,
        adminOnly: false,
        content: () => <LocationsCard />,
      },
    ],
  },
  {
    key: 'team',
    title: 'Team',
    areas: [
      {
        slug: 'team',
        title: 'Team und Rechte',
        description: 'Wer zur Kanzlei gehört, wer sie verwaltet und welche Teams es gibt.',
        icon: Users,
        adminOnly: true,
        content: () => (
          <CardGroup>
            <MembersCard />
            <TeamsCard />
          </CardGroup>
        ),
      },
    ],
  },
];

/**
 * The settings of the firm, bundled by topic: its look, taking on clients, mail, how other
 * systems reach it, privacy and the plan. Every area is a page of its own (after Corly) - no
 * page of everything at once.
 */
const SETTINGS_GROUPS: readonly AreaGroup[] = [
  {
    key: 'appearance',
    title: 'Auftritt',
    areas: [
      {
        slug: 'appearance',
        title: 'Erscheinungsbild',
        description: 'Farbe und Logo der Kanzlei auf allen Oberflächen.',
        icon: Palette,
        adminOnly: true,
        content: () => (
          <div className="grid gap-6">
            <AppearanceCard />
            <FirmLogoCard />
          </div>
        ),
      },
      {
        slug: 'contract-documents',
        title: 'Vertragsdokumente',
        description: 'Schrift, Kopf- und Fußzeile und Deckblatt der Vertragsunterlagen.',
        icon: FileText,
        adminOnly: true,
        feature: 'check-point',
        content: () => <DocumentDesignCard />,
      },
      {
        slug: 'portal-domains',
        title: 'Portal-Domains',
        description: 'Eigene Domains für das Mandantenportal.',
        icon: Globe,
        adminOnly: true,
        feature: 'check-point',
        content: () => <DomainsCard />,
      },
      {
        slug: 'portal-legal',
        title: 'Impressum und Datenschutz',
        description: 'Die Rechtstexte der Kanzlei, die das Mandantenportal verlinkt.',
        icon: Scale,
        adminOnly: true,
        feature: 'check-point',
        content: () => <PortalLegalCard />,
      },
    ],
  },
  {
    key: 'client-intake',
    title: 'Mandatsannahme',
    areas: [
      {
        slug: 'inquiries',
        title: 'Anfragen',
        description: 'Wie lange Anfragen ohne Mandat aufbewahrt werden.',
        icon: Inbox,
        adminOnly: false,
        feature: 'check-point',
        content: () => <InquiryRetentionCard />,
      },
      {
        slug: 'target-profile',
        title: 'Zielprofil',
        description: 'Wen die Kanzlei annimmt – Anfragen werden damit vorsortiert.',
        icon: Target,
        adminOnly: false,
        feature: 'check-point',
        content: () => <TargetProfileCard />,
      },
      {
        slug: 'catalog',
        title: 'Leistungen und Honorare',
        description: 'Was die Kanzlei anbietet und zu welchem Preis – aus der Bausteinbibliothek.',
        icon: ClipboardList,
        adminOnly: false,
        feature: 'check-point',
        content: () => <CatalogCard />,
      },
      {
        slug: 'intake-automation',
        title: 'Automatisierte GwG-Prüfung',
        description: 'Was Kanos bei der Mandatsaufnahme selbst erledigt.',
        icon: Workflow,
        adminOnly: false,
        feature: 'check-point',
        content: () => <IntakeAutomationCard />,
      },
      {
        slug: 'intakes',
        title: 'Erinnerungen & Fristen',
        description:
          'Wann Kanos Mandanten erinnert, der Kanzlei einen Hinweis gibt und ein Mandat ohne neue Aufnahme fortsetzt.',
        icon: BellRing,
        adminOnly: false,
        feature: 'check-point',
        content: () => (
          <div className="grid gap-6">
            <ReminderRules />
            <MandateResumptionCard />
          </div>
        ),
      },
    ],
  },
  {
    key: 'mail',
    title: 'E-Mail',
    areas: [
      {
        slug: 'email',
        title: 'E-Mail',
        description: 'Vorlagen, Aussehen, Versand und Zustellung.',
        icon: Mail,
        adminOnly: true,
        content: () => <MailSettings />,
      },
    ],
  },
  {
    key: 'interfaces',
    title: 'Schnittstellen',
    areas: [
      {
        slug: 'api-clients',
        title: 'API-Zugänge',
        description: 'Zugänge eigener Systeme zur öffentlichen API.',
        icon: KeyRound,
        adminOnly: true,
        feature: 'integrations',
        content: () => <ApiClientsCard />,
      },
      {
        slug: 'webhooks',
        title: 'Webhooks',
        description: 'Kanos meldet Änderungen an Systeme der Kanzlei.',
        icon: Webhook,
        adminOnly: true,
        feature: 'integrations',
        content: () => <WebhooksCard />,
      },
      {
        slug: 'partner-apps',
        title: 'Partner-Apps',
        description: 'Geprüfte Anwendungen von Partnern, mit der Kanzlei verbunden.',
        icon: Blocks,
        adminOnly: true,
        feature: 'integrations',
        content: () => <PartnerAppsCard />,
      },
      {
        slug: 'sandbox',
        title: 'Sandbox',
        description: 'Eine Testkanzlei mit Beispieldaten für Schnittstellen.',
        icon: FlaskConical,
        adminOnly: true,
        feature: 'integrations',
        content: () => <SandboxCard />,
      },
    ],
  },
  {
    key: 'privacy',
    title: 'Datenschutz',
    areas: [
      {
        slug: 'data-export',
        title: 'Datenexport',
        description: 'Alle Daten der Kanzlei als Archiv, etwa für einen Wechsel.',
        icon: Archive,
        adminOnly: true,
        content: () => <ExportCard />,
      },
      {
        slug: 'disclosure',
        title: 'Auskunft',
        description: 'Für Team und Berufsträger (Art. 15 DSGVO) – Mandanten auf ihrer Seite.',
        icon: FileUser,
        adminOnly: true,
        content: () => <DisclosureCard />,
      },
      {
        slug: 'erasure',
        title: 'Löschung',
        description: 'Für Team und Berufsträger (Art. 17 DSGVO) – Mandanten auf ihrer Seite.',
        icon: Eraser,
        adminOnly: true,
        content: () => <ErasureCard />,
      },
      {
        slug: 'audit-log',
        title: 'Protokoll',
        description: 'Wer was wann in der Kanzlei getan hat (Audit-Log).',
        icon: ScrollText,
        adminOnly: true,
        content: () => <AuditCard />,
      },
    ],
  },
  {
    key: 'billing',
    title: 'Abrechnung & Recht',
    areas: [
      {
        slug: 'billing',
        title: 'Abonnement',
        description: 'Plan, Zahlungseinstellungen und Rechnungen.',
        icon: CreditCard,
        adminOnly: true,
        content: () => <BillingCard />,
      },
      {
        slug: 'billing-details',
        title: 'Rechnungsdaten',
        description: 'Wie die Rechnungen von Kanos an die Kanzlei adressiert sind.',
        icon: Receipt,
        adminOnly: true,
        content: () => <BillingDetailsCard />,
      },
      {
        slug: 'processing-agreement',
        title: 'Rechtsdokumente',
        description: 'AVV, AGB, Datenschutzerklärung und Impressum von Kanos.',
        icon: Signature,
        adminOnly: false,
        content: () => <LegalDocuments />,
      },
    ],
  },
];

/** The places with areas of their own, each listing them beside its pages. */
export const AREA_PLACES = [
  { base: '/firm', title: 'Kanzlei', groups: FIRM_GROUPS },
  { base: '/settings', title: 'Einstellungen', groups: SETTINGS_GROUPS },
] as const;

export type AreaBase = (typeof AREA_PLACES)[number]['base'];

/** Every area of the firm and its settings - by slug, each once. */
export const ALL_AREAS = AREA_PLACES.flatMap((place) =>
  place.groups.flatMap((group) => group.areas.map((area) => ({ base: place.base, area }))),
);

/** Where an area lives: the firm's own data under „Kanzlei“, everything else in the settings. */
export const baseOf = (slug: string): AreaBase =>
  ALL_AREAS.find((entry) => entry.area.slug === slug)?.base ?? '/settings';

/** The areas of a place the person may open: administrators all, members what is not theirs. */
export function areasFor(base: AreaBase, firmAdmin: boolean) {
  const place = AREA_PLACES.find((p) => p.base === base);
  return (place?.groups ?? [])
    .map((group) => ({
      ...group,
      areas: group.areas.filter((area) => firmAdmin || !area.adminOnly),
    }))
    .filter((group) => group.areas.length > 0);
}

/** The sections of the firm app; their areas are listed beside their pages. */
export function sectionsFor(firmAdmin: boolean): NavSection[] {
  return [
    ...AREA_PLACES.map(({ base, title }) => ({ base, title, groups: areasFor(base, firmAdmin) })),
    { base: '/profile', title: 'Profil', groups: [...PROFILE_GROUPS, ...FIRM_PROFILE_GROUPS] },
  ];
}
